import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import message from 'antd/lib/message'
import classnames from 'classnames'

import { apiManager } from 'helpers'
import { Button, CloseIcon, Points, ProfileImage } from 'components'
import Modal from 'components/AuthModal/components/Modal'
import Overlay from 'components/AuthModal/components/Overlay'
import { OVERLAY } from 'components/AuthModal/styles'
import { generatePath, Link } from 'react-router-dom'
import { ROUTES } from 'helpers/constants'

const MIN_POINTS = 10

const BTN_CLASS = 'rounded-full max-w-full py-3 text-base font-bold md:text-lg'

const PointsCounter = props => (
  <button
    className="bg-transparent flex items-center justify-center w-10 text-2xl hover:opacity-60"
    {...props}
  />
)

const LikesModal = ({ show, reply, onClose, profile }) => {
  const [points, setPoints] = useState(MIN_POINTS)
  const [comment, setComment] = useState('')
  const [isLoading, setIsLoading] = useState()

  useEffect(() => {
    setPoints(profile?.points || MIN_POINTS)
    setComment(profile?.comment || '')
  }, [profile, show])

  const handleCommentChange = e => setComment(e.target.value)
  const handlePointsChange = decrement => setPoints(_points => points + (decrement ? -1 : 1))

  const handleProfileLike = useCallback(
    async liked => {
      setIsLoading(true)

      try {
        const { matched } = await apiManager.likeProfile(profile.id, {
          liked,
          ...(reply ? null : { points, comment }),
        })
        if (matched) {
          message.success('You matched!')
        }
        onClose(true)
      } catch (error) {
        message.error(error?.message || 'An error occurred, please try again')
      } finally {
        setIsLoading(false)
      }
    },
    [profile, points, comment, reply, onClose]
  )

  return (
    <Overlay show={show} className={OVERLAY} preventEscBackdropClose={true} onClose={onClose}>
      <Modal innerClassName="rounded-t-3xl overflow-y-scroll bg-woodsmokeBlackDarker md:rounded-2xl">
        <div className="relative text-white w-full md:w-108">
          <div className="py-6 px-5 flex flex-col items-center">
            <div className="flex justify-between mb-8 w-full">
              <div className="flex pr-4">
                <Link
                  to={
                    profile.username
                      ? generatePath(ROUTES.PUBLIC_PROFILE, { slug: profile.username })
                      : '#'
                  }
                  target="_blank"
                >
                  <ProfileImage
                    src={profile.profileImage}
                    className="h-14 w-14 rounded-full object-cover mr-3"
                  />
                </Link>
                {!reply && (
                  <div className="opacity-50 text-base">
                    The more token you give, the more love you will receive in return
                  </div>
                )}
              </div>
              <button
                className="bg-white bg-opacity-20 w-6 h-6 flex-shrink-0 rounded-full flex items-center justify-center"
                onClick={() => onClose()}
              >
                <CloseIcon className="w-2 h-2 text-white" />
              </button>
            </div>
            <div className="flex items-center mb-5 w-full">
              {!reply && (
                <div className="flex items-center bg-white bg-opacity-10 rounded-lg mr-4">
                  <PointsCounter
                    onClick={() => handlePointsChange(true)}
                    disabled={points === MIN_POINTS}
                  >
                    -
                  </PointsCounter>
                  <div className="h-4 w-0.5 bg-white bg-opacity-20" />
                  <PointsCounter onClick={() => handlePointsChange()}>+</PointsCounter>
                </div>
              )}
              <Points points={points} />
            </div>
            <textarea
              className="bg-white bg-opacity-5 border-0 rounded-lg w-full p-4 text-base mb-7 focus:border-0 focus:outline-none"
              placeholder="Add a comment"
              value={comment}
              disabled={reply}
              onChange={handleCommentChange}
            />
            <div
              className={classnames('flex items-center w-full space-x-2 flex-row md:space-x-8', {
                'justify-center': !reply,
              })}
            >
              {reply && (
                <Button
                  className={classnames(
                    BTN_CLASS,
                    'border border-white border-solid border-opacity-30 w-1/2'
                  )}
                  onClick={() => handleProfileLike(false)}
                >
                  Decline
                </Button>
              )}
              <Button
                className={classnames(BTN_CLASS, {
                  'w-1/2': reply,
                  'w-80': !reply,
                })}
                onClick={() => handleProfileLike(true)}
                loading={isLoading}
                style={{
                  background: 'linear-gradient(135deg, #EB4335 0%, #EB436F 100%)',
                }}
              >
                {reply ? 'Accept & Reply' : 'Send Request'}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </Overlay>
  )
}

LikesModal.propTypes = {
  show: PropTypes.bool,
  reply: PropTypes.bool,
  profile: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default LikesModal
